import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Layers,
  GitBranch,
  ArrowRight,
  Target,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  FileText,
  Zap,
  Database,
  ArrowUpCircle,
  Scale,
  Shield,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { buildClassificationCardsFromConfig } from '../../utils/fiveSClassificationCards';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Family display metadata for cards (labels/icons only — thresholds come from API)
const FAMILY_META = {
  isolated: { label: 'ISOLATED', color: 'emerald', icon: Target },
  structural: { label: 'STRUCTURAL', color: 'orange', icon: Layers },
  recovery: { label: 'RECOVERY', color: 'blue', icon: ArrowUpCircle },
  structural_recovery: { label: 'STRUCTURAL + RECOVERY', color: 'purple', icon: GitBranch },
  data_breach: { label: 'DATA BREACH METHOD', color: 'red', icon: Shield },
};

function withFamilyIcons(cards) {
  return (cards || []).map((card) => {
    const meta = FAMILY_META[card.classification] || {};
    return { ...card, ...meta };
  });
}

// Round escalation info
const ROUND_ESCALATION = [
  {
    round: 1,
    label: 'Round 1 - Initial Dispute',
    color: 'emerald',
    deadline: '30 days',
    tone: 'Formal',
    approach: 'Compliance test - initial request for reinvestigation',
    statutes: ['15 U.S.C. § 1681e(b)', '15 U.S.C. § 1681i'],
  },
  {
    round: 2,
    label: 'Round 2 - Escalation',
    color: 'amber',
    deadline: '15 days',
    tone: 'Assertive',
    approach: 'Post-dispute noncompliance - Notice of failure to properly reinvestigate',
    statutes: ['15 U.S.C. § 1681i', '15 U.S.C. § 1681n'],
    escalation: 'Notice of Noncompliance',
  },
  {
    round: 3,
    label: 'Round 3 - Pre-Litigation',
    color: 'red',
    deadline: '15 days',
    tone: 'Demanding',
    approach: 'Final notice - willful noncompliance, CFPB complaint reference',
    statutes: ['15 U.S.C. § 1681n', '15 U.S.C. § 1681o'],
    escalation: 'Final Notice Before Legal Action',
  },
];

const ClassificationCard = ({ rule }) => {
  const colorClasses = {
    emerald: 'bg-emerald-600/20 border-emerald-600/50 text-emerald-400',
    orange: 'bg-orange-600/20 border-orange-600/50 text-orange-400',
    blue: 'bg-blue-600/20 border-blue-600/50 text-blue-400',
    purple: 'bg-purple-600/20 border-purple-600/50 text-purple-400',
    red: 'bg-red-600/20 border-red-600/50 text-red-400',
  };
  
  const Icon = rule.icon;
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[rule.color]} bg-stone-900/50`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[rule.color]} flex items-center justify-center`}>
          <Icon size={20} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">{rule.label}</h3>
          <p className="text-xs text-slate-400">{rule.classification}</p>
        </div>
      </div>
      
      <div className="mb-3 p-2 bg-black/30 rounded-lg">
        <p className="text-xs text-slate-500 mb-1">Threshold Rule</p>
        <code className="text-sm text-amber-300">{rule.threshold}</code>
      </div>

      <div className="space-y-1">
        {rule.rules.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
            <CheckCircle size={12} className="text-green-500" />
            <span>{r}</span>
          </div>
        ))}
      </div>

      {rule.note && (
        <p className="mt-3 text-xs text-slate-400 italic">{rule.note}</p>
      )}
    </div>
  );
};

const RoundCard = ({ roundInfo }) => {
  const colorClasses = {
    emerald: 'border-emerald-600/50',
    amber: 'border-amber-600/50',
    red: 'border-red-600/50',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[roundInfo.color]} bg-stone-900/50`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-white">{roundInfo.label}</h3>
        <span className={`px-2 py-1 text-xs rounded-full ${
          roundInfo.round === 1 ? 'bg-emerald-600/30 text-emerald-400' :
          roundInfo.round === 2 ? 'bg-amber-600/30 text-amber-400' :
          'bg-red-600/30 text-red-400'
        }`}>
          {roundInfo.deadline} deadline
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-2 bg-black/30 rounded-lg">
          <p className="text-xs text-slate-500">Tone</p>
          <p className="text-sm text-white font-medium">{roundInfo.tone}</p>
        </div>
        {roundInfo.escalation && (
          <div className="p-2 bg-black/30 rounded-lg">
            <p className="text-xs text-slate-500">Escalation</p>
            <p className="text-sm text-amber-400 font-medium">{roundInfo.escalation}</p>
          </div>
        )}
      </div>
      
      <p className="text-sm text-slate-400 mb-2">{roundInfo.approach}</p>
      
      <div className="flex flex-wrap gap-1">
        {roundInfo.statutes.map((s, i) => (
          <code key={i} className="text-xs bg-stone-800 text-teal-300 px-2 py-0.5 rounded">
            {s}
          </code>
        ))}
      </div>
    </div>
  );
};

const StrategyMethodRow = ({ strategy }) => {
  const classColors = {
    isolated: 'text-emerald-400 bg-emerald-600/20',
    structural: 'text-orange-400 bg-orange-600/20',
    recovery: 'text-blue-400 bg-blue-600/20',
    structural_recovery: 'text-purple-400 bg-purple-600/20',
    collection: 'text-blue-400 bg-blue-600/20',
    debt_validation: 'text-blue-400 bg-blue-600/20',
    factual: 'text-emerald-400 bg-emerald-600/20',
    bankruptcy: 'text-orange-400 bg-orange-600/20',
    data_breach: 'text-red-400 bg-red-600/20',
  };
  
  // Extract classification from various possible fields
  const getClassification = () => {
    if (strategy.classification) return strategy.classification;
    // Try to infer from key or method
    const keyLower = (strategy.key || '').toLowerCase();
    const methodLower = (strategy.method || '').toLowerCase();
    if (keyLower.includes('collection') || methodLower.includes('debt_validation')) return 'recovery';
    if (keyLower.includes('bankruptcy')) return 'structural';
    if (keyLower.includes('inquiry')) return 'isolated';
    if (keyLower.includes('structural')) return 'structural';
    return 'factual';
  };
  
  const classification = getClassification();
  
  // Extract round from key if available
  const getRound = () => {
    if (strategy.round) return strategy.round;
    const keyLower = (strategy.key || '').toLowerCase();
    if (keyLower.includes('round_1') || keyLower.includes('round1')) return 1;
    if (keyLower.includes('round_2') || keyLower.includes('round2')) return 2;
    if (keyLower.includes('round_3') || keyLower.includes('round3')) return 3;
    if (strategy.escalation_required) return 2;
    return 1;
  };
  
  const round = getRound();
  
  return (
    <tr className="border-b border-stone-800 hover:bg-stone-800/50">
      <td className="py-3 px-4">
        <code className="text-sm text-cyan-400">{strategy.key}</code>
      </td>
      <td className="py-3 px-4 text-white font-medium">{strategy.title}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 text-xs rounded-full ${classColors[classification] || 'text-slate-400 bg-slate-600/20'}`}>
          {classification || 'N/A'}
        </span>
      </td>
      <td className="py-3 px-4 text-center">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold ${
          round === 1 ? 'bg-emerald-600/30 text-emerald-400' :
          round === 2 ? 'bg-amber-600/30 text-amber-400' :
          round === 3 ? 'bg-red-600/30 text-red-400' :
          'bg-slate-600/30 text-slate-400'
        }`}>
          {round || '-'}
        </span>
      </td>
      <td className="py-3 px-4">
        <code className="text-xs text-slate-400">{strategy.method || 'N/A'}</code>
      </td>
      <td className="py-3 px-4">
        <span className={`px-2 py-0.5 text-xs rounded ${
          strategy.status === 'active' ? 'bg-green-600/20 text-green-400' : 'bg-slate-600/20 text-slate-400'
        }`}>
          {strategy.status}
        </span>
      </td>
    </tr>
  );
};

const FiveSClassificationPage = () => {
  const { getAuthHeaders } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [fiveSConfig, setFiveSConfig] = useState(null);
  const [configDraft, setConfigDraft] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [runtimeHealth, setRuntimeHealth] = useState(null);

  const STRATEGY_ENDPOINT = `${API}/admin-ops/strategy-mappings`;
  const FIVE_S_CONFIG_ENDPOINT = `${API}/admin-rules/five-s-config`;

  const fetchFiveSConfig = async () => {
    const response = await axios.get(FIVE_S_CONFIG_ENDPOINT, {
      headers: getAuthHeaders(),
      withCredentials: true,
      params: { ensure: true },
    });
    const config = response.data.config;
    setFiveSConfig(config);
    setConfigDraft({
      isolated: { ...(config.isolated || {}) },
      structural: { ...(config.structural || {}) },
      recovery: { ...(config.recovery || {}) },
      structural_recovery: { ...(config.structural_recovery || {}) },
      data_breach: { ...(config.data_breach || {}) },
    });
    setRuntimeHealth(config.runtime_health || null);
    setConfigError(null);
    return config;
  };

  const saveFiveSConfig = async () => {
    if (!configDraft) return;
    try {
      setSavingConfig(true);
      const response = await axios.put(FIVE_S_CONFIG_ENDPOINT, configDraft, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const config = response.data.config;
      setFiveSConfig(config);
      setConfigDraft({
        isolated: { ...(config.isolated || {}) },
        structural: { ...(config.structural || {}) },
        recovery: { ...(config.recovery || {}) },
        structural_recovery: { ...(config.structural_recovery || {}) },
        data_breach: { ...(config.data_breach || {}) },
      });
      setRuntimeHealth(config.runtime_health || null);
      toast.success(`Five S config saved (v${config.version})`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail;
      setConfigError(msg || error.message);
      toast.error(msg || 'Failed to save Five S config');
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      await fetchFiveSConfig();

      const response = await axios.get(STRATEGY_ENDPOINT, {
        headers: getAuthHeaders(),
        withCredentials: true,
        params: { ensure: true, limit: 200 },
      });

      const data = response.data.mappings || [];
      const meta = response.data.meta;

      setStrategies(data);
      setBackendAvailable(true);

      if (meta) {
        setStats({
          total: meta.total,
          active: meta.active,
          classifications: meta.supported_classifications,
          byClassification: meta.by_classification || {},
          byRound: meta.by_round || { 1: 0, 2: 0, 3: 0 },
          canonicalActive: meta.canonical_active,
          canonicalExpected: meta.canonical_expected,
        });
      } else {
        const activeStrategies = data.filter((s) => s.status === 'active');
        const byClassification = {};
        const byRound = { 1: 0, 2: 0, 3: 0 };

        activeStrategies.forEach((s) => {
          const cls = s.classification || 'other';
          byClassification[cls] = (byClassification[cls] || 0) + 1;
          if (s.round) byRound[s.round] = (byRound[s.round] || 0) + 1;
        });

        setStats({
          total: data.length,
          active: activeStrategies.length,
          classifications: 5,
          byClassification,
          byRound,
          canonicalActive: activeStrategies.length,
          canonicalExpected: 12,
        });
      }
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
      const status = error.response?.status;
      setLoadError({
        message: error.response?.data?.detail || error.message || 'Unknown error',
        status: status || 'network',
        endpoint: STRATEGY_ENDPOINT,
      });
      setBackendAvailable(false);
      setStats(null);
      setStrategies([]);
      toast.error('Failed to load strategy methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const classificationCards = withFamilyIcons(
    buildClassificationCardsFromConfig(configDraft || fiveSConfig)
  );
  const healthStatus = runtimeHealth?.status || (fiveSConfig?.validation?.ok ? 'healthy' : 'unknown');

  return (
    <div className="space-y-8 p-6" data-testid="five-s-classification-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Scale className="text-amber-500" size={28} />
            Five S Classification Control
          </h1>
          <p className="text-slate-400 mt-1">
            Classification engine rules, round escalation logic, and active strategy methods
          </p>
          {fiveSConfig && (
            <p className="text-xs text-slate-500 mt-2">
              Version {fiveSConfig.version} · Updated by {fiveSConfig.updated_by || '—'} ·{' '}
              {fiveSConfig.updated_at || '—'} · Runtime health: {healthStatus}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={saveFiveSConfig} disabled={savingConfig || !configDraft} className="gap-2">
            {savingConfig ? 'Saving…' : 'Save Five S Config'}
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Output Format Notice */}
      <div className="p-4 rounded-xl border border-teal-600/50 bg-teal-600/10">
        <div className="flex items-start gap-3">
          <FileText className="text-teal-400 mt-1" size={20} />
          <div>
            <h3 className="font-bold text-white mb-1">White-Sheet Output Format Enforced</h3>
            <p className="text-sm text-slate-300">
              All factual dispute letters now use plain text white-sheet format. No summary boxes, cards, or table layouts.
              Backend controls final letter structure, section order, signature placement, and enclosures.
              Templates render in Times New Roman, 12pt, justified text on white background.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {!backendAvailable && (
        <div className="p-4 rounded-xl border border-red-600/50 bg-red-600/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 mt-0.5" size={20} />
            <div>
              <h3 className="font-bold text-white">Backend unavailable</h3>
              <p className="text-sm text-slate-300 mt-1">
                Strategy method metrics could not be loaded. Metrics are hidden to avoid showing misleading zero counts.
              </p>
              {loadError && (
                <p className="text-xs text-slate-400 mt-2 font-mono">
                  {loadError.endpoint} — HTTP {String(loadError.status)} — {loadError.message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {backendAvailable && stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-stone-900/50 border border-stone-800">
            <p className="text-slate-400 text-sm">Total Strategies</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="p-4 rounded-xl bg-stone-900/50 border border-stone-800">
            <p className="text-slate-400 text-sm">Active</p>
            <p className="text-3xl font-bold text-green-400">{stats.active}</p>
          </div>
          <div className="p-4 rounded-xl bg-stone-900/50 border border-stone-800">
            <p className="text-slate-400 text-sm">Classifications</p>
            <p className="text-3xl font-bold text-amber-400">{stats.classifications}</p>
          </div>
          <div className="p-4 rounded-xl bg-stone-900/50 border border-stone-800">
            <p className="text-slate-400 text-sm">Round Coverage</p>
            <div className="flex gap-2 mt-1">
              <span className="text-emerald-400">R1: {stats.byRound[1] || 0}</span>
              <span className="text-amber-400">R2: {stats.byRound[2] || 0}</span>
              <span className="text-red-400">R3: {stats.byRound[3] || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Classification Rules Section */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <GitBranch className="text-orange-400" size={20} />
          Classification Threshold Rules
        </h2>
        <p className="text-slate-400 text-sm mb-4">
          Thresholds and flags below are loaded from <code className="text-cyan-400">GET /api/admin-rules/five-s-config</code>
          {' '}(source: admin_five_s_config). Saving writes a new versioned record used by runtime classification.
        </p>
        {configError && (
          <div className="mb-4 p-3 rounded-lg border border-red-600/40 bg-red-600/10 text-red-200 text-sm">
            {typeof configError === 'string' ? configError : JSON.stringify(configError)}
          </div>
        )}
        {configDraft && (
          <div className="mb-4 grid grid-cols-2 gap-4 p-4 rounded-xl bg-stone-900/50 border border-stone-800">
            <label className="text-sm text-slate-300">
              Isolated min lates
              <input
                type="number"
                className="mt-1 w-full rounded bg-stone-950 border border-stone-700 px-2 py-1"
                value={configDraft.isolated.min_late_payments ?? 1}
                onChange={(e) =>
                  setConfigDraft((d) => ({
                    ...d,
                    isolated: { ...d.isolated, min_late_payments: Number(e.target.value) },
                  }))
                }
              />
            </label>
            <label className="text-sm text-slate-300">
              Isolated max lates
              <input
                type="number"
                className="mt-1 w-full rounded bg-stone-950 border border-stone-700 px-2 py-1"
                value={configDraft.isolated.max_late_payments ?? 2}
                onChange={(e) =>
                  setConfigDraft((d) => ({
                    ...d,
                    isolated: { ...d.isolated, max_late_payments: Number(e.target.value) },
                  }))
                }
              />
            </label>
            <label className="text-sm text-slate-300">
              Structural min lates
              <input
                type="number"
                className="mt-1 w-full rounded bg-stone-950 border border-stone-700 px-2 py-1"
                value={configDraft.structural.min_late_payments ?? 3}
                onChange={(e) =>
                  setConfigDraft((d) => ({
                    ...d,
                    structural: { ...d.structural, min_late_payments: Number(e.target.value) },
                  }))
                }
              />
            </label>
            <label className="text-sm text-slate-300 flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={!!configDraft.structural_recovery.combine_when_same_bureau}
                onChange={(e) =>
                  setConfigDraft((d) => ({
                    ...d,
                    structural_recovery: {
                      ...d.structural_recovery,
                      combine_when_same_bureau: e.target.checked,
                    },
                  }))
                }
              />
              Combine Structural + Recovery when same bureau
            </label>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {classificationCards.map(rule => (
            <ClassificationCard key={rule.classification} rule={rule} />
          ))}
        </div>
      </div>


      {/* Round Escalation Section */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="text-amber-400" size={20} />
          Round-Aware Escalation Logic
        </h2>
        <p className="text-slate-400 text-sm mb-4">
          Each round uses different tone, deadlines, and legal pressure based on the dispute escalation path.
        </p>
        <div className="grid grid-cols-3 gap-4">
          {ROUND_ESCALATION.map(round => (
            <RoundCard key={round.round} roundInfo={round} />
          ))}
        </div>
      </div>

      {/* Source of Truth Flow */}
      <div className="p-6 rounded-xl bg-stone-900/50 border border-stone-800">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <ArrowRight className="text-cyan-400" size={20} />
          Active Source of Truth Flow
        </h2>
        <div className="flex items-center justify-between gap-2 text-center">
          <div className="flex-1 p-3 bg-teal-600/20 rounded-lg border border-teal-600/50">
            <p className="text-xs text-slate-400">Step 1</p>
            <p className="text-sm font-bold text-teal-400">Classification</p>
            <p className="text-xs text-slate-500">_classify_damage()</p>
          </div>
          <ArrowRight className="text-slate-600" size={16} />
          <div className="flex-1 p-3 bg-orange-600/20 rounded-lg border border-orange-600/50">
            <p className="text-xs text-slate-400">Step 2</p>
            <p className="text-sm font-bold text-orange-400">Strategy</p>
            <p className="text-xs text-slate-500">strategy_methods DB</p>
          </div>
          <ArrowRight className="text-slate-600" size={16} />
          <div className="flex-1 p-3 bg-amber-600/20 rounded-lg border border-amber-600/50">
            <p className="text-xs text-slate-400">Step 3</p>
            <p className="text-sm font-bold text-amber-400">Round</p>
            <p className="text-xs text-slate-500">round_number param</p>
          </div>
          <ArrowRight className="text-slate-600" size={16} />
          <div className="flex-1 p-3 bg-purple-600/20 rounded-lg border border-purple-600/50">
            <p className="text-xs text-slate-400">Step 4</p>
            <p className="text-sm font-bold text-purple-400">Prompt/Template</p>
            <p className="text-xs text-slate-500">render_*_letter()</p>
          </div>
          <ArrowRight className="text-slate-600" size={16} />
          <div className="flex-1 p-3 bg-emerald-600/20 rounded-lg border border-emerald-600/50">
            <p className="text-xs text-slate-400">Step 5</p>
            <p className="text-sm font-bold text-emerald-400">Output</p>
            <p className="text-xs text-slate-500">White-Sheet HTML</p>
          </div>
        </div>
        <p className="text-sm text-slate-400 mt-4">
          Classification selects the strategy. Strategy selects the round-aware prompt. The resolved admin prompt
          controls final letter order.
        </p>
      </div>

      {/* Strategy Methods Table */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Database className="text-cyan-400" size={20} />
          Active Strategy Methods (Database Records)
        </h2>
        
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading strategy methods...</div>
        ) : loadError ? (
          <div className="p-8 rounded-xl border border-red-600/40 bg-red-600/10 text-center">
            <AlertTriangle className="mx-auto text-red-400 mb-3" size={24} />
            <p className="text-white font-medium">Failed to load strategy methods</p>
            <p className="text-sm text-slate-400 mt-2 font-mono">
              {loadError.endpoint}
            </p>
            <p className="text-sm text-slate-400 font-mono">
              HTTP {String(loadError.status)} — {loadError.message}
            </p>
            <Button onClick={fetchData} variant="outline" className="mt-4 gap-2">
              <RefreshCw size={16} />
              Retry
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-stone-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-800/50">
                <tr>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Key</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Title</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Classification</th>
                  <th className="py-3 px-4 text-center text-slate-400 font-medium">Round</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Method</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {strategies.filter((s) => s.status === 'active').length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      <p>No active strategy methods returned from the database.</p>
                      <p className="text-xs text-slate-600 mt-2 font-mono">
                        GET {STRATEGY_ENDPOINT}?ensure=true&amp;limit=200
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Total records in response: {strategies.length}. Check seed status or record filters.
                      </p>
                    </td>
                  </tr>
                ) : (
                  strategies
                    .filter((s) => s.status === 'active')
                    .sort((a, b) => {
                      if (a.classification !== b.classification) {
                        return (a.classification || '').localeCompare(b.classification || '');
                      }
                      return (a.round || 0) - (b.round || 0);
                    })
                    .map((strategy) => (
                      <StrategyMethodRow key={strategy.key || strategy.id} strategy={strategy} />
                    ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Implementation Status — driven by real runtime health */}
      <div
        className={`p-4 rounded-xl border ${
          healthStatus === 'healthy'
            ? 'border-emerald-600/50 bg-emerald-600/10'
            : 'border-amber-600/50 bg-amber-600/10'
        }`}
      >
        <div className="flex items-start gap-3">
          {healthStatus === 'healthy' ? (
            <CheckCircle className="text-emerald-400 mt-1" size={20} />
          ) : (
            <AlertTriangle className="text-amber-400 mt-1" size={20} />
          )}
          <div>
            <h3 className="font-bold text-white mb-1">Five S Runtime Health</h3>
            <p className="text-sm text-slate-300 mb-3">
              Status: <span className="font-mono text-white">{healthStatus}</span>
              {runtimeHealth?.detail ? ` — ${runtimeHealth.detail}` : ''}
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <ul className="text-slate-300 space-y-1">
                <li>
                  • Active Five S config:{' '}
                  {runtimeHealth?.active_config_present || fiveSConfig?.active ? 'yes' : 'missing'}
                </li>
                <li>• Config version: {fiveSConfig?.version ?? runtimeHealth?.active_version ?? '—'}</li>
                <li>• Rule source: {fiveSConfig?.classification_rule_source || 'admin_five_s_config'}</li>
                <li>
                  • Validation:{' '}
                  {fiveSConfig?.validation?.ok ? 'ok' : (fiveSConfig?.validation?.errors || []).join('; ') || 'unknown'}
                </li>
              </ul>
              <ul className="text-slate-300 space-y-1">
                <li>
                  • Strategy methods active:{' '}
                  {runtimeHealth?.strategy_methods_seeded ?? stats?.canonicalActive ?? '—'}
                </li>
                <li>
                  • Canonical expected:{' '}
                  {stats?.canonicalExpected ?? '—'}
                </li>
                <li>• Cache present: {runtimeHealth?.cache_present ? 'yes' : 'no'}</li>
                <li>• Cache version: {runtimeHealth?.cache_version ?? '—'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiveSClassificationPage;
